import React from 'react';

import cs from 'classnames';
import moment from 'moment';

import {AttachTooltip} from 'app/general/overlays';
import {gt} from 'app/utils/i18n';

const getIconAndText = (status) => {
  const map = {
    saved: {
      color: '',
      text: gt('Saved'),
      icon: 'fa-check',
    },
    saving: {
      color: '',
      text: gt('Saving'),
      icon: 'fa-circle-notch fa-spin',
    },
    error: {
      color: 'text-danger',
      text: gt('Not saved'),
      icon: 'fa-times',
    },
    editing: {
      color: '',
      text: gt('Editing'),
      icon: 'fa-pen',
    },
  };
  return map[status];
};

const getElapsedTime = (timestamp) =>
  moment.min([moment(timestamp), moment()]).fromNow();

const RETRY_INTERVALS = [10, 30, 60, 120, 300];
type Props = {
  status: 'editing' | 'saved' | 'saving' | 'error';
  lastModified: string;
  updateVersion: () => Promise<any>;
  className?: 'string';
};
type State = {
  lastSavedSecs: number;
  retryOnSecs: number;
};

class ObjectSaveStatus extends React.PureComponent<Props, State> {
  state = {
    lastSavedSecs: 0,
    retryOnSecs: 0,
  };
  timers: {
    lastSaved?: number;
    retryCounter?: number;
    retryCall?: number;
  } = {
    lastSaved: undefined,
    retryCounter: undefined,
  };
  currentInterval?: number;

  getNextInterval(): number {
    const intervalIndex = RETRY_INTERVALS.indexOf(
      this.currentInterval ?? RETRY_INTERVALS[0]
    );
    const interval: number =
      intervalIndex !== RETRY_INTERVALS.length - 1
        ? RETRY_INTERVALS[intervalIndex + 1]
        : this.currentInterval ?? RETRY_INTERVALS[0];
    this.currentInterval = interval;

    this.setState({
      retryOnSecs: this.currentInterval ?? RETRY_INTERVALS[0],
    });
    return interval;
  }

  startRetryTimers = () => {
    clearInterval(this.timers.retryCounter);
    clearInterval(this.timers.retryCall);
    this.setState({
      retryOnSecs: this.getNextInterval(),
    });
    // @ts-expect-error Timeout doesn't exist in dom
    this.timers.retryCounter = setInterval(this.retryTick, 1000);
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.status === 'error' &&
      this.props.status !== 'error' &&
      this.timers.retryCounter === undefined
    ) {
      this.startRetryTimers();
    } else if (nextProps.status === 'saved' && this.props.status !== 'saved') {
      this.clearRetryIntervals();
    }
  }

  clearRetryIntervals() {
    clearInterval(this.timers.retryCounter);
    clearInterval(this.timers.retryCall);
    this.timers.retryCounter = undefined;
    this.timers.retryCall = undefined;
    this.currentInterval = undefined;
  }

  retryTick = () => {
    this.setState(
      {
        retryOnSecs: this.state.retryOnSecs - 1,
      },
      () => {
        if (this.state.retryOnSecs < 1) {
          clearInterval(this.timers.retryCounter);
          this.retry();
        }
      }
    );
  };
  tick = () => {
    this.setState({
      lastSavedSecs: this.state.lastSavedSecs + 1,
    });
  };

  componentDidMount() {
    // @ts-expect-error Timeout doesn't exist in dom
    this.timers.lastSaved = setInterval(this.tick, 10000);
  }

  componentWillUnmount() {
    clearInterval(this.timers.lastSaved);
    this.clearRetryIntervals();
  }

  getTooltipContent() {
    return this.props.status !== 'error' ? (
      gt('Every change you make is automatically saved.')
    ) : (
      <div>
        <div>{gt("We couldn't save your changes.")}</div>
        <div>{gt('Retrying in %s seconds...', this.state.retryOnSecs)}</div>
      </div>
    );
  }

  retry = () => this.props.updateVersion().catch(this.startRetryTimers);

  render() {
    const {status, lastModified, className} = this.props;
    const data = getIconAndText(status);

    if (!data) {
      return null;
    }

    return (
      <AttachTooltip
        placement="bottom"
        tooltipContent={
          <div>
            {this.getTooltipContent()}{' '}
            {gt('Last saved edit was %s', getElapsedTime(lastModified))}.
          </div>
        }
      >
        {(handlers) => (
          <span
            className={cs(
              'navbar-text text-nowrap btn-sm py-0',
              className,
              data.color
            )}
            {...handlers}
          >
            <i className={`fa ${data.icon}`} />
            <span className="d-none d-md-inline"> {data.text}</span>
            {this.props.status === 'error' ? (
              <span>
                {' - '}
                <a
                  className="retry"
                  href=""
                  onClick={(e) => {
                    e.preventDefault();
                    this.retry();
                  }}
                >
                  {gt('Retry')}
                </a>
              </span>
            ) : null}
          </span>
        )}
      </AttachTooltip>
    );
  }
}

export default ObjectSaveStatus;
